import type { VariableNode } from 'services/entities/VariablesEntity';
import type { CrmBindingFrame } from './crmChipBindings';
import { isGroupedArrayNode } from './deriveCrmRows';

// One real CRM instance node of a grouped array, at any depth, with its ancestry preserved
// instead of merged away. `deriveCrmRows.ts`'s `buildInstanceFrames` collapses a whole
// ancestor chain into one flat leaf binding per deepest instance; this module exists so group
// row placement can still ask "which group instance encloses this service instance?".
export type CrmInstance = {
  arrayTemplate: string; // grouped array this instance belongs to
  objectId: string;
  frame: CrmBindingFrame; // ancestors' own leaves merged, then this instance's
  leaves: Record<string, VariableNode>; // only leaves whose template is in the chip set
  // SELF-INCLUSIVE: maps this instance's own arrayTemplate -> own objectId, plus every
  // enclosing array's template -> that ancestor's objectId. Self-inclusion is what makes
  // same-array pairing and strict-descendant pairing one code path.
  ancestorObjectIdByArrayTemplate: Record<string, string>;
};

// Walks `root` depth-first, emitting one CrmInstance per instance node of EVERY grouped array
// (not just the first array a pattern row happens to reference) — the limitation this module
// exists to lift relative to buildInstanceFrames.
export function collectCrmInstances(root: VariableNode, chipTemplates: ReadonlySet<string>): CrmInstance[] {
  const instances: CrmInstance[] = [];

  function walkInstances(
    arrayNode: VariableNode,
    ancestorFrame: CrmBindingFrame,
    ancestorLeaves: Record<string, VariableNode>,
    ancestorObjectIdByArrayTemplate: Record<string, string>
  ): void {
    const arrayTemplate = arrayNode.template;
    if (!arrayTemplate) return;

    (arrayNode.variables ?? []).forEach((instance) => {
      if (instance.objectId === undefined) return;
      const instanceObjectId = instance.objectId;

      const ownFrame: Record<string, string> = {};
      const ownLeaves: Record<string, VariableNode> = {};
      const nestedArrays: VariableNode[] = [];

      // Canonical structure requires custom walk: stop at grouped arrays (queued below), only
      // map non-array children to objectId. Mirrors deriveCrmRows.ts's buildInstanceFrames,
      // except every nested array is queued here — not just the first one a chip references.
      function walk(node: VariableNode): void {
        (node.variables ?? []).forEach((child) => {
          if (isGroupedArrayNode(child)) {
            nestedArrays.push(child);
            return;
          }
          if (child.template) {
            ownFrame[child.template] = instanceObjectId;
            if (chipTemplates.has(child.template)) {
              ownLeaves[child.template] = child;
            }
          }
          walk(child);
        });
      }
      walk(instance);

      const frame: CrmBindingFrame = { ...ancestorFrame, ...ownFrame };
      const leaves = { ...ancestorLeaves, ...ownLeaves };
      const nextAncestorObjectIdByArrayTemplate = { ...ancestorObjectIdByArrayTemplate, [arrayTemplate]: instanceObjectId };

      instances.push({
        arrayTemplate,
        objectId: instanceObjectId,
        frame,
        leaves,
        ancestorObjectIdByArrayTemplate: nextAncestorObjectIdByArrayTemplate,
      });

      nestedArrays.forEach((nested) => walkInstances(nested, frame, leaves, nextAncestorObjectIdByArrayTemplate));
    });
  }

  function findArrays(node: VariableNode): void {
    (node.variables ?? []).forEach((child) => {
      if (isGroupedArrayNode(child)) {
        walkInstances(child, {}, {}, {});
        return;
      }
      findArrays(child);
    });
  }

  findArrays(root);

  return instances;
}

function chipBelongsToArrayTemplate(chipTemplate: string, arrayTemplate: string): boolean {
  return chipTemplate.startsWith(`${arrayTemplate}.`);
}

// Longest grouped-array template that prefixes at least one chip template. Evaluated chip by
// chip in `chipTemplates` order: the first chip with any matching array wins, and among its
// matches the deepest (longest) array template wins — `...groups.opportunityservices.displayname`
// selects `...groups.opportunityservices`, not `...groups`.
// Deliberate simplification, not an oversight: mirrors the existing flat-repeat feature's own
// "first array wins when multiple arrays are chipped in one row" convention.
export function selectArrayTemplateForChips(chipTemplates: readonly string[], instances: readonly CrmInstance[]): string | undefined {
  const arrayTemplates = Array.from(new Set(instances.map((instance) => instance.arrayTemplate)));

  for (const chipTemplate of chipTemplates) {
    const matches = arrayTemplates.filter((arrayTemplate) => chipBelongsToArrayTemplate(chipTemplate, arrayTemplate));
    if (matches.length === 0) continue;
    return matches.reduce((longest, candidate) => (candidate.length > longest.length ? candidate : longest));
  }
  return undefined;
}

export function isDescendantArrayTemplate(childTemplate: string, parentTemplate: string): boolean {
  return childTemplate === parentTemplate || childTemplate.startsWith(`${parentTemplate}.`);
}
